import React, { useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useHighlights, useBookmarks, useParagraphExplanations, HighlightableParagraph } from './HighlightableText';
import OverviewRuler from './OverviewRuler';
import './StudyGuide.css';

export default function DocumentReader({ documentId, title, intro, parts }) {
  const { highlights, error: highlightsError, addHighlight, removeHighlight } = useHighlights(documentId);
  const { bookmarks, error: bookmarksError, addBookmark, removeBookmark } = useBookmarks(documentId);
  const explanations = useParagraphExplanations(documentId);
  const nodesRef = useRef({});

  const registerNode = useCallback((paragraphId, node) => {
    if (node) {
      nodesRef.current[paragraphId] = node;
    } else {
      delete nodesRef.current[paragraphId];
    }
  }, []);

  const jumpToParagraph = useCallback((paragraphId) => {
    const node = nodesRef.current[paragraphId];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const highlightsByParagraph = highlights.reduce((map, h) => {
    (map[h.paragraph_id] = map[h.paragraph_id] || []).push(h);
    return map;
  }, {});

  const bookmarksByParagraph = bookmarks.reduce((map, b) => {
    map[b.paragraph_id] = b;
    return map;
  }, {});

  return (
    <div className="study-guide-page study-reader-page">
      <Link to="/study-guide" className="study-reader-back">&larr; Back to Study Guide</Link>
      <h1>{title}</h1>
      {intro && <p className="study-guide-intro">{intro}</p>}
      <p className="study-reader-hint">
        Select any text below to highlight it, or use the flag next to a paragraph to bookmark your place &mdash;
        both are saved to your account. Paragraphs with an AI explanation available show a star (✦) next to the
        flag &mdash; click it for a quick, plain-language explanation. The strip along the right edge shows where
        your highlights (gold) and bookmarks (navy) are in the document; click a mark to jump straight there.
      </p>
      {highlightsError && <p className="study-guide-error">{highlightsError}</p>}
      {bookmarksError && <p className="study-guide-error">{bookmarksError}</p>}

      <details className="study-reader-nav">
        <summary>Jump to section</summary>
        <ul>
          {parts.flatMap((part) => part.sections).map((section) => (
            <li key={section.id}>
              <a href={`#${section.id}`}>{section.heading}</a>
            </li>
          ))}
        </ul>
      </details>

      {parts.map((part) => (
        <div key={part.id}>
          {part.title && <h2 className="study-part-title">{part.title}</h2>}
          {part.sections.map((section) => (
            <div className="study-section" id={section.id} key={section.id}>
              <h3>{section.heading}</h3>
              {section.sourceNote && <p className="study-section-source">{section.sourceNote}</p>}
              {section.paragraphs.map((text, idx) => {
                const paragraphId = `${section.id}-p${idx}`;
                const bookmark = bookmarksByParagraph[paragraphId];
                return (
                  <HighlightableParagraph
                    key={paragraphId}
                    paragraphId={paragraphId}
                    text={text}
                    highlights={highlightsByParagraph[paragraphId] || []}
                    onCreate={addHighlight}
                    onDelete={removeHighlight}
                    isBookmarked={Boolean(bookmark)}
                    onToggleBookmark={() => (bookmark ? removeBookmark(bookmark.id) : addBookmark(paragraphId))}
                    registerNode={registerNode}
                    paragraphExplanation={explanations[paragraphId] || null}
                  />
                );
              })}
            </div>
          ))}
        </div>
      ))}

      <OverviewRuler nodesRef={nodesRef} highlights={highlights} bookmarks={bookmarks} onJump={jumpToParagraph} />
    </div>
  );
}
